import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { republishViewPatientsRealtime } from "actions/view-patient-action";
import { setViewPatientRealtimeDescriptor } from "actions/view-patient-action";
import { serializeViewPatientPersistence } from "actions/view-patient-action";
import { updateOrgCountsAndBalances } from "actions/user-action";
import { updateViewPatient } from "database/offlineDb/indexDb";
import { getReportStatus } from "helpers/CommonPatient";
import { useMlStatusRepository } from "./mlStatusContext";
import { createPatientStatusAdapter } from "./patientStatusAdapter";
import { projectPatientStatus } from "./patientProjection";

const isNonemptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const sameMrn = (left, right) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const canonicalLaterality = (laterality) => {
  if (laterality === "left" || laterality === "OS") return "left";
  if (laterality === "right" || laterality === "OD") return "right";
  return null;
};

const configuredAlgorithms = (algorithms) => {
  const source = Array.isArray(algorithms)
    ? algorithms
    : typeof algorithms === "string"
    ? algorithms.split(",")
    : [];
  const values = source.map((value) =>
    typeof value === "string" ? value.trim() : ""
  );
  return values.length > 0 &&
    values.every(isNonemptyString) &&
    new Set(values).size === values.length
    ? values.sort()
    : null;
};

const RETRYABLE_PATIENT_STATUSES = new Set([
  "Pending",
  "Unprocessed",
  "Error-CNF",
]);

const SETTLEMENT_TERMINAL_PATIENT_STATUSES = new Set([
  "NRML",
  "NODR",
  "D1",
  "D2",
  "NOF",
  "UND",
  "Error in Preprocess",
]);

const KNOWN_PATIENT_STATUSES = new Set([
  ...RETRYABLE_PATIENT_STATUSES,
  ...SETTLEMENT_TERMINAL_PATIENT_STATUSES,
]);

const canProjectPatientStatusTransition = (currentStatus, nextStatus) => {
  if (currentStatus === "Pending") {
    return (
      RETRYABLE_PATIENT_STATUSES.has(nextStatus) ||
      SETTLEMENT_TERMINAL_PATIENT_STATUSES.has(nextStatus)
    );
  }
  return (
    (currentStatus === "Unprocessed" || currentStatus === "Error-CNF") &&
    SETTLEMENT_TERMINAL_PATIENT_STATUSES.has(nextStatus)
  );
};

const durableImageIdentity = (row, descriptor) => {
  const details = row?.retinalImagesData?.retinalImagesDetails;
  if (details === undefined || details === null) return new Set();
  if (typeof details !== "object" || Array.isArray(details)) return null;
  const identities = new Set();
  for (const [laterality, field] of [
    ["left", "imageLeftEye"],
    ["right", "imageRightEye"],
  ]) {
    const images = details[field];
    if (images !== undefined && !Array.isArray(images)) return null;
    for (const image of images || []) {
      const guid = image?.fileData?.guid || image?.data?.guid || image?.guid;
      const url =
        image?.imgUrl ||
        image?.url ||
        image?.imageUrl ||
        image?.fileData?.imageUrl;
      const explicitOrgIds = [
        image?.orgz_id,
        image?.data?.orgz_id,
        image?.fileData?.orgz_id,
      ].filter((value) => value !== undefined && value !== null);
      const key = `${guid}|${laterality}`;
      if (
        !isNonemptyString(guid) ||
        !isNonemptyString(url) ||
        explicitOrgIds.some(
          (orgId) => Number(orgId) !== Number(descriptor.orgId)
        ) ||
        identities.has(key)
      ) {
        return null;
      }
      identities.add(key);
    }
  }
  return identities;
};

const identitiesByMrn = (evidence) => {
  const identities = new Map();
  if (!(evidence instanceof Map)) return identities;
  for (const item of evidence.values()) {
    const mrn = String(item.mrn);
    const rowIdentities = identities.get(mrn) || new Set();
    rowIdentities.add(`${item.guid}|${item.laterality}`);
    identities.set(mrn, rowIdentities);
  }
  return identities;
};

const activeDescriptor = (descriptor) => {
  if (
    !descriptor ||
    (descriptor.mode !== "date" && descriptor.mode !== "search") ||
    !Number.isSafeInteger(descriptor.page) ||
    descriptor.page < 0 ||
    !Number.isSafeInteger(descriptor.pageSize) ||
    descriptor.pageSize < 1 ||
    typeof descriptor.annotationOnly !== "boolean" ||
    !Array.isArray(descriptor.activeMrns) ||
    descriptor.activeMrns.length === 0
  ) {
    return null;
  }
  const identityKeys = descriptor.activeMrns.map(String);
  if (new Set(identityKeys).size !== identityKeys.length) return null;
  return {
    ...descriptor,
    activeMrns: [...descriptor.activeMrns],
  };
};

const imageEvidence = (
  rows,
  descriptor,
  algorithms,
  acceptedStatuses = KNOWN_PATIENT_STATUSES,
  frozenIdentities = null
) => {
  if (
    !Array.isArray(rows) ||
    !descriptor ||
    !Array.isArray(descriptor.activeMrns) ||
    !Array.isArray(algorithms) ||
    !(acceptedStatuses instanceof Set) ||
    (frozenIdentities !== null && !(frozenIdentities instanceof Map))
  ) {
    return null;
  }
  const activeMrns = new Set(descriptor.activeMrns.map(String));
  const configuredModels = new Set(algorithms);
  const evidence = new Map();
  for (const row of rows) {
    if (!activeMrns.has(String(row?.mrn))) continue;
    if (
      row?.orgz_id !== undefined &&
      Number(row.orgz_id) !== Number(descriptor.orgId)
    ) {
      return null;
    }

    const frozenRowIdentities = frozenIdentities?.get(String(row.mrn));
    if (
      frozenRowIdentities !== undefined &&
      !(frozenRowIdentities instanceof Set)
    ) {
      return null;
    }
    const durableIdentities = durableImageIdentity(row, descriptor);
    if (!durableIdentities) return null;
    const visibleIdentity = new Set([
      ...(frozenRowIdentities || []),
      ...durableIdentities,
    ]);
    const visibleEvidence = new Map();
    const emptyUrlVisibleTuples = new Set();
    for (const laterality of ["left", "right"]) {
      const images = row?.[laterality];
      if (images !== undefined && !Array.isArray(images)) return null;
      for (const image of images || []) {
        const source = image?.data || image;
        const guid = source?.guid;
        const model = source?.Model;
        const imageOrgId = source?.orgz_id;
        const imageLaterality = canonicalLaterality(source?.laterality);
        if (
          !isNonemptyString(guid) ||
          !isNonemptyString(model) ||
          (imageOrgId !== undefined &&
            imageOrgId !== null &&
            Number(imageOrgId) !== Number(descriptor.orgId)) ||
          imageLaterality !== laterality
        ) {
          return null;
        }
        const identityKey = `${guid}|${laterality}`;
        const hasVisibleUrl = isNonemptyString(image?.url);
        if (!hasVisibleUrl && !visibleIdentity.has(identityKey)) return null;
        if (hasVisibleUrl) visibleIdentity.add(identityKey);
        if (!KNOWN_PATIENT_STATUSES.has(source?.status)) return null;
        const tupleKey = `${identityKey}|${model}`;
        if (visibleEvidence.has(tupleKey)) return null;
        if (!hasVisibleUrl) emptyUrlVisibleTuples.add(tupleKey);
        visibleEvidence.set(tupleKey, {
          guid,
          laterality,
          model,
          mrn: String(row.mrn),
          status: source.status,
        });
      }
    }

    const normalizedEvidence = new Map(visibleEvidence);
    const backupEvidence = new Map();
    if (row?.backUpReport !== undefined && row?.backUpReport !== null) {
      if (
        typeof row.backUpReport !== "object" ||
        !Array.isArray(row.backUpReport.images)
      ) {
        return null;
      }
      for (const image of row.backUpReport.images) {
        const source = image?.data || image;
        const guid = source?.guid;
        const model = source?.Model;
        if (!isNonemptyString(model)) return null;
        const laterality = canonicalLaterality(source?.laterality);
        const imageOrgId = source?.orgz_id;
        const identityKey = `${guid}|${laterality}`;
        const tupleKey = `${identityKey}|${model}`;
        if (
          !isNonemptyString(guid) ||
          !KNOWN_PATIENT_STATUSES.has(source?.status) ||
          !visibleIdentity.has(identityKey) ||
          (imageOrgId !== undefined &&
            imageOrgId !== null &&
            Number(imageOrgId) !== Number(descriptor.orgId)) ||
          backupEvidence.has(tupleKey)
        ) {
          return null;
        }
        backupEvidence.set(tupleKey, {
          guid,
          laterality,
          model,
          mrn: String(row.mrn),
          status: source.status,
        });
      }
      for (const [key, item] of backupEvidence) {
        const visible = normalizedEvidence.get(key);
        if (visible && visible.status !== item.status) return null;
        normalizedEvidence.set(key, item);
      }
    }
    if (
      Array.from(emptyUrlVisibleTuples).some(
        (key) => !backupEvidence.has(key)
      )
    ) {
      return null;
    }

    for (const [key, item] of normalizedEvidence) {
      if (evidence.has(key)) return null;
      if (acceptedStatuses.has(item.status)) evidence.set(key, item);
    }
  }
  return evidence.size > 0 ? evidence : null;
};

const allConfiguredStatusesSettled = (patient, algorithms) => {
  const expected = new Set(algorithms);
  const backupImages = patient?.backUpReport?.images;
  const images =
    Array.isArray(backupImages) && backupImages.length > 0
      ? backupImages
      : [patient?.left, patient?.right]
          .filter(Array.isArray)
          .reduce((all, eye) => [...all, ...eye], []);
  const relevant = images
    .map((image) => image?.data || image)
    .filter((image) => expected.has(image?.Model));
  return (
    relevant.length > 0 &&
    relevant.every((image) =>
      SETTLEMENT_TERMINAL_PATIENT_STATUSES.has(image?.status)
    )
  );
};

const isRetryablePatientTuple = (patient, event) => {
  const images = [
    ...(Array.isArray(patient?.left) ? patient.left : []),
    ...(Array.isArray(patient?.right) ? patient.right : []),
    ...(Array.isArray(patient?.backUpReport?.images)
      ? patient.backUpReport.images
      : []),
  ];
  return images.some(
    (image) => {
      const source = image?.data || image;
      return (
        source?.guid === event?.guid &&
        source?.Model === event?.model &&
        (source?.laterality === event?.laterality ||
          (event?.laterality === "left" && source?.laterality === "OS") ||
          (event?.laterality === "right" && source?.laterality === "OD")) &&
        canProjectPatientStatusTransition(source?.status, event?.status)
      );
    }
  );
};

const confirmedWrite = (result) =>
  result === true || (typeof result === "number" && result > 0);

const defaultSnapshot = async (
  descriptor,
  context,
  writePatient,
  rows,
  userStore,
  republish,
  updateBalances,
  reportStatus
) => {
  const targets = context?.event
    ? rows.filter((row) =>
        [row?.left, row?.right]
          .filter(Array.isArray)
          .some((eye) =>
            eye.some(
              (image) =>
                image?.data?.guid === context.event.guid &&
                (image?.data?.laterality === context.event.laterality ||
                  (image?.data?.laterality === "OS" &&
                    context.event.laterality === "left") ||
                  (image?.data?.laterality === "OD" &&
                    context.event.laterality === "right"))
            )
          )
      )
    : rows;
  if (!context.isCurrent() || targets.length === 0) {
    return { outcome: "ignored" };
  }
  await Promise.all(
    targets.map((row) =>
      reportStatus(
        { ...row, restDetailOnServer: true },
        true,
        {
          userStore,
          getAllViewPatientsFromIDB: () => republish(context),
          getPatientsList: () => republish(context),
          updateOrgCountsAndBalances: () => updateBalances?.(context),
          writeViewPatient: writePatient,
        },
        true,
        context
      )
    )
  );
  return context.isCurrent()
    ? { outcome: "applied", descriptor }
    : { outcome: "ignored" };
};

export const ViewPatientRealtimeOwner = ({
  algorithms,
  authToken,
  clearDescriptor,
  descriptor,
  getRows,
  location,
  republish,
  reportStatus = getReportStatus,
  rows = [],
  snapshotDescriptor,
  serializeWrite = serializeViewPatientPersistence,
  updateBalances,
  userStore,
  writePatient = updateViewPatient,
  adapterFactory = createPatientStatusAdapter,
}) => {
  const repository = useMlStatusRepository();
  const pathname = location?.pathname || "";
  const authTokenRef = useRef(authToken);
  const frozenIdentitiesRef = useRef({
    authToken: null,
    scopeKey: null,
    identities: new Map(),
  });
  const authChanged = authTokenRef.current !== authToken;
  const normalizedDescriptor = authChanged ? null : activeDescriptor(descriptor);
  const normalizedAlgorithms = configuredAlgorithms(algorithms);
  const identityScopeKey =
    pathname === "/view-patients" &&
    normalizedDescriptor &&
    normalizedAlgorithms &&
    isNonemptyString(authToken)
      ? `${JSON.stringify(normalizedDescriptor)}:${normalizedAlgorithms.join(
          ","
        )}`
      : null;
  const frozenIdentities =
    frozenIdentitiesRef.current.authToken === authToken &&
    frozenIdentitiesRef.current.scopeKey === identityScopeKey
      ? frozenIdentitiesRef.current.identities
      : null;
  const currentRows =
    typeof getRows === "function" ? getRows() || [] : rows || [];
  const evidence =
    pathname === "/view-patients" && normalizedDescriptor && normalizedAlgorithms
      ? imageEvidence(
          currentRows,
          normalizedDescriptor,
          normalizedAlgorithms,
          KNOWN_PATIENT_STATUSES,
          frozenIdentities
        )
      : null;
  const scopeKey =
    evidence && isNonemptyString(authToken)
      ? `view-patient:${JSON.stringify(normalizedDescriptor)}:${Array.from(
          evidence.keys()
        ).sort()}:${normalizedAlgorithms.join(",")}`
      : null;
  // A cached row still awaiting an authoritative status (Pending/Unprocessed/
  // Error-CNF) has missed every socket event emitted before this mount, so the
  // initial snapshot is the only chance to reconcile it without a reload.
  const hasRetryableEvidence =
    evidence instanceof Map &&
    Array.from(evidence.values()).some((item) =>
      RETRYABLE_PATIENT_STATUSES.has(item?.status)
    );
  const rowsRef = useRef(currentRows);
  const algorithmsRef = useRef(algorithms);
  const descriptorRef = useRef(normalizedDescriptor);
  const republishRef = useRef(republish);
  const reportStatusRef = useRef(reportStatus);
  const serializeWriteRef = useRef(serializeWrite);
  const snapshotRef = useRef(snapshotDescriptor);
  const updateBalancesRef = useRef(updateBalances);
  const userStoreRef = useRef(userStore);
  const writePatientRef = useRef(writePatient);
  const adapterFactoryRef = useRef(adapterFactory);
  rowsRef.current = currentRows;
  algorithmsRef.current = algorithms;
  descriptorRef.current = normalizedDescriptor;
  republishRef.current = republish;
  reportStatusRef.current = reportStatus;
  serializeWriteRef.current = serializeWrite;
  snapshotRef.current = snapshotDescriptor;
  updateBalancesRef.current = updateBalances;
  userStoreRef.current = userStore;
  writePatientRef.current = writePatient;
  adapterFactoryRef.current = adapterFactory;

  useEffect(() => {
    if (!identityScopeKey) {
      frozenIdentitiesRef.current = {
        authToken,
        scopeKey: null,
        identities: new Map(),
      };
      return;
    }
    if (evidence) {
      frozenIdentitiesRef.current = {
        authToken,
        scopeKey: identityScopeKey,
        identities: identitiesByMrn(evidence),
      };
    } else if (
      frozenIdentitiesRef.current.authToken !== authToken ||
      frozenIdentitiesRef.current.scopeKey !== identityScopeKey
    ) {
      frozenIdentitiesRef.current = {
        authToken,
        scopeKey: identityScopeKey,
        identities: new Map(),
      };
    }
  }, [authToken, evidence, identityScopeKey]);

  useEffect(() => {
    if (
      authTokenRef.current !== authToken &&
      descriptor &&
      typeof clearDescriptor === "function"
    ) {
      clearDescriptor(null);
    }
    authTokenRef.current = authToken;
  }, [authToken, clearDescriptor, descriptor]);

  useEffect(() => {
    if (!repository || !scopeKey) return undefined;
    let eventGeneration = 0;
    let snapshotGeneration = 0;
    let active = true;
    const writePatientInOrder = (patient) => {
      return serializeWriteRef.current(
        () => (active ? writePatientRef.current(patient) : 0),
        () => active
      );
    };
    const writtenPatients = [];
    const writeEventPatient = async (patient) => {
      const result = await writePatientInOrder(patient);
      if (confirmedWrite(result)) writtenPatients.push(patient);
      return result;
    };
    const acceptedEvidence = new Map(evidence);
    const acceptedModels = new Set(normalizedAlgorithms);
    const adapter = adapterFactoryRef.current({
      writePatient: writeEventPatient,
      republish: (scope) => {
        const patient = writtenPatients.shift();
        return republishRef.current(patient ? { ...scope, patient } : scope);
      },
      projectPatient: (patient, event, configured) =>
        isRetryablePatientTuple(patient, event)
          ? projectPatientStatus(patient, event, configured)
          : { outcome: "ignored" },
    });
    const frozenIdentitiesForScope = () => {
      const frozen = frozenIdentitiesRef.current;
      return frozen.authToken === authTokenRef.current &&
        frozen.scopeKey === identityScopeKey
        ? frozen.identities
        : null;
    };
    const runSnapshot = (context) => {
      const acceptedEventGeneration = eventGeneration;
      const acceptedSnapshotGeneration = ++snapshotGeneration;
      const snapshotContext = {
        ...context,
        isCurrent: () =>
          context.isCurrent() &&
          eventGeneration === acceptedEventGeneration &&
          snapshotGeneration === acceptedSnapshotGeneration,
      };
      const frozenDescriptor = descriptorRef.current;
      const snapshotEvidence = imageEvidence(
        rowsRef.current,
        frozenDescriptor,
        normalizedAlgorithms,
        RETRYABLE_PATIENT_STATUSES,
        frozenIdentitiesForScope()
      );
      const activeMrns = new Set(
        snapshotEvidence
          ? Array.from(snapshotEvidence.values()).map((item) => item.mrn)
          : []
      );
      const activeRows = rowsRef.current.filter((row) =>
        activeMrns.has(String(row?.mrn))
      );
      if (!snapshotContext.isCurrent() || activeRows.length === 0) {
        return Promise.resolve({ outcome: "ignored" });
      }
      if (typeof snapshotRef.current === "function") {
        return snapshotRef.current(
          frozenDescriptor,
          snapshotContext,
          writePatientInOrder
        );
      }
      return defaultSnapshot(
        frozenDescriptor,
        snapshotContext,
        writePatientInOrder,
        activeRows,
        userStoreRef.current,
        republishRef.current,
        updateBalancesRef.current,
        reportStatusRef.current
      );
    };
    const unsubscribe = repository.subscribe({
      scopeKey,
      accepts: (event) => {
        if (!acceptedModels.has(event?.model)) return false;
        const retryableEvidence = imageEvidence(
          rowsRef.current,
          descriptorRef.current,
          normalizedAlgorithms,
          RETRYABLE_PATIENT_STATUSES,
          frozenIdentitiesForScope()
        );
        return Boolean(
          retryableEvidence?.has(
            `${event?.guid}|${event?.laterality}|${event?.model}`
          )
        );
      },
      initialSnapshot: hasRetryableEvidence ? runSnapshot : undefined,
      project: async (event, context) => {
        eventGeneration += 1;
        const target = acceptedEvidence.get(
          `${event?.guid}|${event?.laterality}|${event?.model}`
        );
        const result = await adapter.apply({
          getPatient: () =>
            rowsRef.current.find((row) => sameMrn(row?.mrn, target?.mrn)),
          event,
          algorithms: algorithmsRef.current,
          isCurrent: context.isCurrent,
        });
        if (
          result?.outcome === "applied" &&
          context.isCurrent() &&
          allConfiguredStatusesSettled(result.patient, normalizedAlgorithms) &&
          typeof updateBalancesRef.current === "function"
        ) {
          try {
            await updateBalancesRef.current(context);
          } catch (error) {
            // Balance refresh cannot invalidate an already durable projection.
          }
        }
        return result;
      },
      resync: runSnapshot,
    });
    return () => {
      active = false;
      unsubscribe();
      adapter.cleanup();
    };
  }, [identityScopeKey, repository, scopeKey]);

  return null;
};

const mapStateToProps = (store) => ({
  algorithms: store.userStore?.userSettings?.algorithms || [],
  descriptor: store.viewPatientStore?.realtimeDescriptor || null,
  rows: store.viewPatientStore?.viewPatientslist || [],
  userStore: store.userStore,
});

export default connect(mapStateToProps, {
  clearDescriptor: setViewPatientRealtimeDescriptor,
  republish: republishViewPatientsRealtime,
  updateBalances: updateOrgCountsAndBalances,
})(withRouter(ViewPatientRealtimeOwner));
